import { components } from '../.generated';
import { OneService } from '../OneService';

type SiteEntityEntry = components['schemas']['SiteEntityEntry'];

const PAGE_LIMIT = 200;
const MAX_PAGES = 100;

/**
 * Sites provider — site metadata, entities and properties over the Graph CQRS
 * layer. Unlike the device-register/hub provider APIs, the site operations are
 * *generic* graph queries/commands (their discriminator is a bare `GetSiteQuery`
 * etc. carrying `siteId` in the body), so they go through the shared OneService
 * `/graph/*` transport rather than a `/Providers/Cozify.Site/...` endpoint.
 *
 * This is the template for migrating the hand-written `/Providers/Cozify.Sites`
 * REST calls in `commonSlice/thunks.ts`.
 */
export class SitesProvider {
  constructor(private readonly one: OneService) {}

  getSite(siteId: string, signal?: AbortSignal) {
    const query: components['schemas']['GetSiteQuery'] = {
      type: 'GetSiteQuery',
      siteId,
    };
    return this.one.query<components['schemas']['GetSiteQueryResult']>({
      query,
      signal,
    });
  }

  getSiteProperties(siteId: string, signal?: AbortSignal) {
    const query: components['schemas']['GetSitePropertiesQuery'] = {
      type: 'GetSitePropertiesQuery',
      siteId,
    };
    return this.one.query<
      components['schemas']['GetSitePropertiesQueryResult']
    >({ query, signal });
  }

  getSiteDefinition(siteId: string, signal?: AbortSignal) {
    const query: components['schemas']['GetSiteDefinitionQuery'] = {
      type: 'GetSiteDefinitionQuery',
      siteId,
    };
    return this.one.query<
      components['schemas']['GetSiteDefinitionQueryResult']
    >({ query, signal });
  }

  setSiteProperties(
    siteId: string,
    properties: components['schemas']['SitePropertiesEntry'],
    signal?: AbortSignal
  ) {
    const command: components['schemas']['SetSitePropertiesCommand'] = {
      type: 'SetSitePropertiesCommand',
      siteId,
      properties,
    };
    return this.one.command<
      components['schemas']['SetSitePropertiesCommandResult']
    >({ command, signal });
  }

  /** List every entity under a site, paging through the full result set. */
  async listSiteEntities(
    siteId: string,
    globPattern = '**'
  ): Promise<SiteEntityEntry[]> {
    const all: SiteEntityEntry[] = [];
    for (let page = 0; page < MAX_PAGES; page++) {
      const res = await this.listSiteEntitiesPage(
        siteId,
        globPattern,
        page * PAGE_LIMIT,
        PAGE_LIMIT
      );
      const entries = res.entries ?? [];
      all.push(...entries);
      if (
        (res.totalCount !== undefined && all.length >= res.totalCount) ||
        entries.length < PAGE_LIMIT
      )
        break;
    }
    return all;
  }

  listSiteEntitiesPage(
    siteId: string,
    globPattern: string,
    skip: number,
    limit: number,
    signal?: AbortSignal
  ) {
    const query: components['schemas']['ListSiteEntitiesQuery'] = {
      type: 'ListSiteEntitiesQuery',
      siteId,
      globPattern,
      skip,
      limit,
    };
    return this.one.query<components['schemas']['ListSiteEntitiesQueryResult']>(
      { query, signal }
    );
  }
}
